'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/context/AuthContext';
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react';
import { AuthLayout, FormInput, SocialLogin, ErrorAlert, WaitlistModal } from '@/components/auth';
import { useTranslations } from 'next-intl';
import { getRedirectUrl } from '@/lib/redirect-url';

const API_BASE_URL = '/api/proxy';

const DASHBOARD_URL = process.env.NEXT_PUBLIC_DASHBOARD_URL || '';

// Firebase Auth throws errors shaped as `{ code: string, message: string }`.
// Narrow the unknown catch value down to that `code` without resorting to `any`.
function getFirebaseErrorCode(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    return String((err as { code: unknown }).code);
  }
  return '';
}

export default function LoginPage() {
  const t = useTranslations('auth');
  const searchParams = useSearchParams();
  const { loginWithGoogle, loginWithEmail, isLoading, redirectSignInCompleted, redirectSignInErrorCode } = useAuth();
  const redirectTo = getRedirectUrl(searchParams.get('redirect'));
  // Forward the redirect target to /register too — an existing user sent here (e.g. from
  // an event invite) who instead needs to register elsewhere must not lose the code by
  // following this link. Only appended when there's an actual target to carry.
  const registerHref = redirectTo !== '/' ? `/register?redirect=${encodeURIComponent(redirectTo)}` : '/register';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [showWaitlist, setShowWaitlist] = useState(() => searchParams.get('error') === 'registration_disabled');

  const getErrorMessage = (code: string) => {
    switch (code) {
      case 'auth/invalid-email': return t('errorInvalidEmail');
      case 'auth/user-disabled': return t('errorUserDisabled');
      case 'auth/user-not-found': return t('errorUserNotFound');
      case 'auth/wrong-password': return t('errorWrongPassword');
      case 'auth/invalid-credential': return t('errorInvalidCredential');
      // Firebase's own throttle on repeated failed sign-ins: the only lockout there is.
      case 'auth/too-many-requests': return t('errorTooManyRequests');
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request': return t('errorCancelled');
      case 'REGISTRATION_DISABLED': return t('errorRegistrationDisabled');
      default: return t('errorGeneral');
    }
  };

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'registration_disabled') {
      setError(t('registrationDisabled'));
    } else if (errorParam === 'email_signup_unavailable') {
      // Set by AuthContext when EMAIL_SIGNUP_UNAVAILABLE strands a just-created Firebase
      // account with no backend User row — deliberately NOT showWaitlist: unlike
      // registration_disabled this isn't an admin-closed gate, just the email service
      // being down, so the waitlist modal would be the wrong call to action here.
      setError(t('emailSignupUnavailable'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Completes the standalone-PWA sign-in flow (see AuthContext's loginWithGoogle /
  // isStandaloneOrInAppBrowser): the click handler that started signInWithRedirect ran on a
  // page that no longer exists after the full-page navigation, so this page's own load is
  // what has to notice the sign-in finished and carry the user on to where they were headed
  // (checkout, etc.) instead of leaving them stranded on /login looking signed in for nothing.
  useEffect(() => {
    if (redirectSignInCompleted) {
      window.location.href = redirectTo.startsWith('http') ? redirectTo : `${DASHBOARD_URL}${redirectTo}`;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectSignInCompleted]);

  // Same flow, failure case: a redirect sign-in that completed but errored (e.g. an
  // account-exists-with-different-credential conflict) previously vanished silently — nothing
  // ever read getRedirectResult's rejection. Now it does, so show it instead of nothing.
  useEffect(() => {
    if (redirectSignInErrorCode) {
      setError(getErrorMessage(redirectSignInErrorCode));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectSignInErrorCode]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/auth/registration-status`)
      .then((r) => r.json())
      .then((data) => setRegistrationEnabled(data.enabled !== false))
      .catch(() => {}); // fail open silently
  }, []);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await loginWithEmail(email, password);
      window.location.href = redirectTo.startsWith('http') ? redirectTo : `${DASHBOARD_URL}${redirectTo}`;
    } catch (err: unknown) {
      setError(getErrorMessage(getFirebaseErrorCode(err)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      await loginWithGoogle();
      window.location.href = redirectTo.startsWith('http') ? redirectTo : `${DASHBOARD_URL}${redirectTo}`;
    } catch (err: unknown) {
      setError(getErrorMessage(getFirebaseErrorCode(err)));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <>
    <AuthLayout
      title={t('loginTitle')}
      subtitle={t('loginSubtitle')}
    >
      <ErrorAlert message={error} />

      <form onSubmit={handleEmailLogin} className="space-y-4">
        <FormInput
          label={t('emailLabel')}
          icon={Mail}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="nama@email.com"
          required
          disabled={isSubmitting}
          autoComplete="email"
        />

        <FormInput
          label={t('passwordLabel')}
          icon={Lock}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('passwordPlaceholder')}
          required
          disabled={isSubmitting}
          autoComplete="current-password"
          rightElement={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isSubmitting}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          }
        />

        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-slate-300 accent-teal-600 focus:ring-teal-500 cursor-pointer"
            />
            <span className="text-slate-600">{t('rememberMe')}</span>
          </label>
          <Link href="/forgot-password" className="text-teal-600 hover:text-teal-700 font-medium transition-colors">
            {t('forgotPassword')}
          </Link>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-orange-500 hover:bg-orange-600 text-white shadow-sm hover:shadow active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>{t('loginBtn')} <ArrowRight className="w-4 h-4" /></>
          )}
        </button>
      </form>

      <div className="mt-6">
        <SocialLogin onGoogleClick={handleGoogleLogin} isLoading={isSubmitting} />
      </div>

      {registrationEnabled && (
        <p className="mt-8 text-center text-sm text-slate-500">
          {t('noAccount')}{' '}
          <Link href={registerHref} className="font-semibold text-teal-600 hover:text-teal-700 transition-colors">
            {t('registerFree')}
          </Link>
        </p>
      )}
    </AuthLayout>

    {showWaitlist && (
      <WaitlistModal
        onClose={() => setShowWaitlist(false)}
        initialEmail={email}
      />
    )}
    </>
  );
}
